import React from 'react'
import { createTheme } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

const primaryColor = '#265665'
const tertiaryT01 = '#6B9CA7'
const tertiaryT02 = '#CDE3E0'
const tertiaryT03 = '#ECF4F3'
const tertiaryT04 = '#DBAA14'
const tertiaryT05 = '#E4CD86'
const tertiaryT08 = '#E16D6D'
const tertiaryT09 = '#75AC99'
const neutralsN00 = '#FFFEFE'
const neutralsN02 = '#FCFCFC'
const neutralsN03 = '#F3F2F2'
const neutralsN03_5 = '#E1DEDE'
const neutralsN04 = '#CECACA'
const neutralsN06 = '#B2ACAC'
const neutralsN08 = '#A2A2A2'
const textLight = '#FDFDFD'
const textDark = '#252121'
const textAccent = '#1F4450'
const textGrayDark = '#938989'
const iconAccent = '#265665'
const buttonPrimaryDark = '#265665'
const buttonPrimaryLight = '#6B9CA7'
const buttonSecondaryDark = '#DDB63E'
const buttonSecondaryLight = '#E4CD86'
const buttonErrorDark = '#F25959'
const buttonErrorLight = '#E16D6D'
const buttonSuccessDark = '#4D8D77'
const buttonSuccessLight = '#75AC99'
const buttonGrayDark = '#B2ACAC'
const buttonGrayLight = '#D8D7D7'
const backgroundGrayLight = '#FCFCFC'
const alertSuccess = '#4D8D77'
const alertError = '#F25959'
// Brandless
const brandlessTextWhite = '#FFFEFE'
const brandlessTextDisabled = '#ABB4BA'
const brandlessTextBlack = '#252121'
const brandlessActionBGWhite = '#FFFEFE'
const brandlessActionBGDisabled = '#E2E6E9'
const brandlessActionBGBlack = '#252121'
const brandlessActionBGBlueMedium = '#4A5565'
const brandlessLinkBlue = '#2A589D'
const brandlessStrokeDisabled = '#ABB4BA'
const brandlessStrokeBlack = '#252121'
const brandlessStrokeBlueMedium = '#475468'
const brandlessIconBlack = '#252121'
// Shadows
const customShadowSoft = '2px 2px 2px 0px rgba(120, 120, 120, 0.08)'

const titleTypography = {
  title_xxl: {
    fontSize: '2.5rem',
    lineHeight: '2.25rem',
    fontWeight: 700,
  },
  title_xl: {
    fontSize: '2rem',
    lineHeight: '1.5rem',
    fontWeight: 700,
  },
  title_lg: {
    fontSize: '2rem',
    lineHeight: '2rem',
    fontWeight: 600,
  },
  title_md: {
    fontSize: '2rem',
    lineHeight: '2.5rem',
    fontWeight: 600,
  },
  title_sm: {
    fontSize: '1.5rem',
    lineHeight: '1.5rem',
    fontWeight: 600,
  },
  title_xs: {
    fontSize: '1.25rem',
    lineHeight: '1.5rem',
    fontWeight: 600,
  },
  title_xxs: {
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    fontWeight: 600,
  },
}

const bodyTypography = {
  body_md_medium: {
    fontSize: '1.5rem',
    lineHeight: '1.875rem',
    fontWeight: 500,
  },
  body_md_semibold: {
    fontSize: '1.5rem',
    lineHeight: '1.5rem',
    fontWeight: 600,
  },
  body_md_bold: {
    fontSize: '1.5rem',
    lineHeight: '1.5rem',
    fontWeight: 700,
  },
  body_sm_medium: {
    fontSize: '1.375rem',
    lineHeight: '1.375rem',
    fontWeight: 500,
  },
  body_sm_semibold: {
    fontSize: '1.375rem',
    lineHeight: '1.625rem',
    fontWeight: 600,
  },
  body_sm_bold: {
    fontSize: '1.375rem',
    lineHeight: '1.375rem',
    fontWeight: 700,
  },
  body_s_medium: {
    fontSize: '1.125rem',
    lineHeight: '1.125rem',
    fontWeight: 500,
  },
  body_s_bold: {
    fontSize: '1.125rem',
    lineHeight: '1.125rem',
    fontWeight: 700,
  },
  body_xs_medium: {
    fontSize: '1rem',
    lineHeight: '1.25rem',
    fontWeight: 500,
  },
  body_xs_semibold: {
    fontSize: '1rem',
    lineHeight: '1.25rem',
    fontWeight: 600,
  },
  body_xs_bold: {
    fontSize: '1rem',
    lineHeight: '1.25rem',
    fontWeight: 700,
  },
  body_xxs_medium: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 500,
  },
  body_xxs_semibold: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 600,
  },
  body_xxs_bold: {
    fontSize: '0.875rem',
    lineHeight: '1.25rem',
    fontWeight: 700,
  },
  caption_light: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 300,
  },
  caption_medium: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 500,
  },
  caption_semibold: {
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 600,
  },
  breadcrumb: {
    fontSize: '0.8rem',
    lineHeight: '1rem',
    fontWeight: 300,
  },
}

let theme = createTheme({
  cssVariables: true,
  mobileHeaderHeight: 40,
  desktopHeaderHeight: 48,
  palette: {
    primary: {
      main: primaryColor,
      contrastText: '#fff',
    },
    secondary: {
      main: '#DDB63E',
    },
    tertiary: {
      t01: {
        color: tertiaryT01,
      },
      t02: {
        color: tertiaryT02,
      },
      t03: {
        color: tertiaryT03,
      },
      t04: {
        color: tertiaryT04,
      },
      t05: {
        color: tertiaryT05,
      },
      t06: {
        color: '#E2D5AD',
      },
      t07: {
        color: '#E3CECD',
      },
      t08: {
        color: tertiaryT08,
      },
      t09: {
        color: tertiaryT09,
      },
    },
    neutrals: {
      n00: {
        color: neutralsN00,
      },
      n01: {
        color: '#F6F6F6',
      },
      n02: {
        color: neutralsN02,
      },
      n03: {
        color: neutralsN03,
      },
      n03_5: {
        color: neutralsN03_5,
      },
      n04: {
        color: neutralsN04,
      },
      n06: {
        color: neutralsN06,
      },
      n08: {
        color: neutralsN08,
      },
      n09: {
        color: '#7F7C7C',
      },
      n10: {
        color: '#262626',
      },
    },
    text: {
      light: {
        color: textLight,
      },
      dark: {
        color: textDark,
      },
      grayLight: {
        color: '#F9F6F6',
      },
      grayMedium: {
        color: '#ADBAC2',
      },
      grayDark: {
        color: textGrayDark,
      },
      accent: {
        color: textAccent,
      },
      accesory: {
        color: '#ECC039',
      },
      error: {
        color: '#E73939',
      },
      success: {
        color: '#478670',
      },
      data: {
        color: '#177D8C',
      },
      warning: {
        color: '#E97E2A',
      },
    },
    icon: {
      dark: {
        color: '#353333',
      },
      light: {
        color: '#FDFCFC',
      },
      grayDark: {
        color: '#7C7575',
      },
      grayLight: {
        color: '#C0CAD0',
      },
      grayMedium: {
        color: '#B7AAAA',
      },
      accent: {
        color: iconAccent,
      },
      error: {
        color: '#ED3838',
      },
      success: {
        color: '#2B7A61',
      },
      warning: {
        color: '#D77E38',
      },
      data: {
        color: '#177D8C',
      },
    },
    button: {
      primaryDark: {
        color: buttonPrimaryDark,
      },
      primaryLight: {
        color: buttonPrimaryLight,
      },
      secondaryDark: {
        color: buttonSecondaryDark,
      },
      secondaryLight: {
        color: buttonSecondaryLight,
      },
      errorDark: {
        color: buttonErrorDark,
      },
      errorLight: {
        color: buttonErrorLight,
      },
      successDark: {
        color: buttonSuccessDark,
      },
      successLight: {
        color: buttonSuccessLight,
      },
      grayDark: {
        color: buttonGrayDark,
      },
      grayLight: {
        color: buttonGrayLight,
      },
    },
    background: {
      default: '#transparent',
      white: {
        color: '#FFFEFE',
      },
      grayLight: {
        color: backgroundGrayLight,
      },
      grayDark: {
        color: '#F5F5F5',
      },
      t01: {
        color: '#CDE3E0',
      },
      t03: {
        color: '#F9F7F1',
      },
      primary: {
        color: '#265665',
      },
      primaryLight: {
        color: '#E2E7EE',
      },
      error: {
        color: '#EB8D8D',
      },
    },
    alert: {
      success: {
        color: alertSuccess,
      },
      error: {
        color: alertError,
      },
      warning: {
        color: '#F29C59',
      },
    },
    panel: {
      info: {
        color: '#E6F1FA',
      },
      success: {
        color: '#E6F9F7',
      },
      warning: {
        color: '#FFF5E8',
      },
      error: {
        color: '#F8E9E9',
      },
    },
    riskStates: {
      accepted: {
        color: '#3BAE4D',
      },
      docerror: {
        color: '#B43E24',
      },
      cancelled: {
        color: '#4A4226',
      },
      rectified: {
        color: '#DF9423',
      },
      lenderaccept: {
        color: '#2B8A90',
      },
      lenderreview: {
        color: '#B770D0',
      },
      pending: {
        color: '#C4A125',
      },
      unassigned: {
        color: '#968F8A',
      },
      fixed: {
        color: '#3D71D1',
      },
    },
    newStates: {
      pending: {
        color: '#8F8989',
      },
      finished: {
        color: '#234F5D',
      },
      correction: {
        color: '#D77E38',
      },
      ongoing: {
        color: '#6B9CA7',
      },
      errorcancel: {
        color: '#F25959',
      },
    },
    graphs: {
      cancelled: {
        color: '#B53B20',
      },
      rejected: {
        color: '#C38322',
      },
      general: {
        household: {
          color: '#266556',
        },
        enterprise: {
          color: '#2A589D',
        },
        community: {
          color: '#782A9D',
        },
      },
      opportunities: {
        opened: {
          color: '#6BA77C',
        },
        config: {
          color: '#19858F',
        },
        suscribe: {
          color: '#B36013',
        },
        formalize: {
          color: '#9C56CE',
        },
        finished: {
          color: '#5885C6',
        },
      },
      provisionals: {
        new: {
          color: '#CA8B41',
        },
        approved: {
          color: '#3B9F75',
        },
        rejected: {
          color: '#D87659',
        },
      },
      projects: {
        opened: {
          color: '#3D71D1',
        },
        revisionFinances: {
          color: '#C13EB4',
        },
        pendingClient: {
          color: '#3A74AF',
        },
        fundingPhase: {
          color: '#336D80',
        },
        subventionValidation: {
          color: '#C41BDE',
        },
        pendingContract: {
          color: '#3E7E7B',
        },
        pendingComm: {
          color: '#6A68CF',
        },
        startInstall: {
          color: '#3A7DA1',
        },
        validateStart: {
          color: '#8F4EE9',
        },
        pendingInstall: {
          color: '#35824A',
        },
        installed: {
          color: '#AB4ACF',
        },
        legalization: {
          color: '#4B8023',
        },
        validateLegalization: {
          color: '#A615BD',
        },
        finished: {
          color: '#437F64',
        },
        signContract: {
          color: '#A7932A',
        },
      },
      payments: {
        pendingdocumentation: {
          color: '#6B9CA7',
        },
        tofix: {
          color: '#F25959',
        },
        pendingvalidate: {
          color: '#6B9CA7',
        },
        waiting: {
          color: '#F29C59',
        },
        released: {
          color: '#D77E38',
        },
        waitingbank: {
          color: '#437F64',
        },
        paid: {
          color: '#4D8D77',
        },
        aboned: {
          color: '#F25959',
        },
        cancelled: {
          color: '#F25959',
        },
      },
    },
    brandless: {
      background: {
        white: {
          color: brandlessTextWhite,
        },
        grayMedium: {
          color: '#E9E9EC',
        },
        grayLight: {
          color: '#F2F2F7',
        },
        grayDark: {
          color: '#595555',
        },
        blueLight: {
          color: '#F6F8FD',
        },
      },
      action_bg: {
        white: {
          color: brandlessActionBGWhite,
        },
        disabled: {
          color: brandlessActionBGDisabled,
        },
        grayDark: {
          color: '#7F7C7C',
        },
        black: {
          color: brandlessActionBGBlack,
        },
        blueMedium: {
          color: brandlessActionBGBlueMedium,
        },
      },
      text: {
        white: {
          color: '#FFFEFE',
        },
        disabled: {
          color: brandlessTextDisabled,
        },
        grayDark: {
          color: '#938989',
        },
        black: {
          color: brandlessTextBlack,
        },
        blueMedium: {
          color: '#475468',
        },
      },
      icon: {
        white: {
          color: '#FFFEFE',
        },
        disabled: {
          color: '#E2E6E9',
        },
        grayDark: {
          color: '#938989',
        },
        blue: {
          color: '#2A589D',
        },
        blueMedium: {
          color: '#65758C',
        },
        black: {
          color: brandlessIconBlack,
        },
      },
      link: {
        blue: {
          color: brandlessLinkBlue,
        },
      },
      alert: {
        error: {
          color: '#E73939',
        },
        success: {
          color: '#2B7A61',
        },
      },
      divider: {
        gray: {
          color: '#F3F4F6',
        },
      },
      stroke: {
        black: {
          color: brandlessStrokeBlack,
        },
        disabled: {
          color: brandlessStrokeDisabled,
        },
        blueMedium: {
          color: brandlessStrokeBlueMedium,
        },
      },
    },
  },
  customShadows: {
    soft: customShadowSoft,
    medium: '2px 4px 4px 0px rgba(0, 0, 0, 0.08)',
    strong: '0px 8px 16px 0px rgba(0, 0, 0, 0.20)',
    field: '0px 1px 5px 0px rgba(49, 43, 43, 0.12)',
  },
  typography: {
    fontFamily: [
      'Urbanist',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    ...titleTypography,
    ...bodyTypography,
  },
  overrides: {
    MuiTableCell: {
      root: {
        paddingTop: '1px',
        paddingBottom: '1px',
        maxWidth: '0 !important',
        minWidth: '0 !important',
      },
    },
    MuiSvgIcon: {
      fontSizeLarge: {
        fontSize: '6rem',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: '35px',
      },
    },
  },
  components: {
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            ':hover': {
              backgroundColor: tertiaryT03 + ' !important',
            },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
        indicator: {
          backgroundColor: primaryColor,
          height: '0.25rem',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          width: '15rem',
          textTransform: 'none',
          ...bodyTypography.body_sm_medium,
          '&.Mui-selected': {
            fontWeight: 600,
            color: primaryColor,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: customShadowSoft,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        bigBrandlessBlack: {
          width: '20.5rem',
          height: '3.125rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGBlack,
          color: brandlessTextWhite,
          ':hover': {
            backgroundColor: brandlessActionBGBlack,
            color: brandlessTextWhite,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        smallBrandlessBlack: {
          width: '10rem',
          height: '3.125rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGBlack,
          color: brandlessTextWhite,
          ':hover': {
            backgroundColor: brandlessActionBGBlack,
            color: brandlessTextWhite,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        bigBrandlessWhite: {
          width: '20.5rem',
          height: '3.125rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGWhite,
          color: brandlessTextBlack,
          border: '1px solid ' + brandlessStrokeBlack,
          ':hover': {
            backgroundColor: brandlessActionBGWhite,
            color: brandlessTextBlack,
            border: '1px solid ' + brandlessStrokeBlack,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        smallBrandlessWhite: {
          width: '10rem',
          height: '3.125rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGWhite,
          color: brandlessTextBlack,
          border: '1px solid ' + brandlessStrokeBlack,
          ':hover': {
            backgroundColor: brandlessActionBGWhite,
            color: brandlessTextBlack,
            border: '1px solid ' + brandlessStrokeBlack,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        bigBrandlessBlue: {
          width: '20.5rem',
          height: '3.125rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGBlueMedium,
          color: brandlessTextWhite,
          ':hover': {
            backgroundColor: brandlessActionBGBlueMedium,
            color: brandlessTextWhite,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        mediumBrandlessBlue: {
          width: '12.875rem',
          height: '2.5rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGBlueMedium,
          color: brandlessTextWhite,
          ':hover': {
            backgroundColor: brandlessActionBGBlueMedium,
            color: brandlessTextWhite,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        smallBrandlessBlue: {
          width: '10rem',
          height: '3.125rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          borderRadius: '0.625rem',
          backgroundColor: brandlessActionBGBlueMedium,
          color: brandlessTextWhite,
          ':hover': {
            backgroundColor: brandlessActionBGBlueMedium,
            color: brandlessTextWhite,
          },
          ':disabled': {
            border: '1px solid ' + brandlessStrokeDisabled,
            backgroundColor: brandlessActionBGDisabled,
            color: brandlessTextDisabled,
          },
        },
        redirectBackground: {
          width: '1.875rem',
          height: '1.875rem',
          border: '1px solid ' + iconAccent,
          borderRadius: '0.25rem',
          backgroundColor: buttonPrimaryLight,
          color: textLight,
          ':hover': {
            backgroundColor: iconAccent,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        redirectOutlined: {
          width: '1.875rem',
          height: '1.875rem',
          border: '1px solid ' + iconAccent,
          borderRadius: '0.25rem',
          backgroundColor: backgroundGrayLight,
          color: iconAccent,
          ':hover': {
            backgroundColor: buttonPrimaryLight,
            color: textLight,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        smallOutlined: {
          width: '8.125rem',
          height: '1.875rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          border: '1px solid ' + buttonPrimaryDark,
          borderRadius: '0.25rem',
          backgroundColor: backgroundGrayLight,
          color: textAccent,
          ':hover': {
            backgroundColor: buttonPrimaryLight,
            color: textLight,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        mediumOutlined: {
          width: '12.875rem',
          height: '2.5rem',
          ...titleTypography.title_xxs,
          textTransform: 'none',
          border: '1px solid ' + buttonPrimaryDark,
          borderRadius: '0.25rem',
          backgroundColor: backgroundGrayLight,
          color: textAccent,
          ':hover': {
            backgroundColor: buttonPrimaryLight,
            color: textLight,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        bigOutlined: {
          width: '15.1875rem',
          height: '3rem',
          ...titleTypography.title_xs,
          textTransform: 'none',
          border: '1px solid ' + buttonPrimaryDark,
          borderRadius: '0.25rem',
          backgroundColor: backgroundGrayLight,
          color: textAccent,
          ':hover': {
            backgroundColor: buttonPrimaryLight,
            color: textLight,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        smallPrimary: {
          width: '8.125rem',
          height: '1.875rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          border: '1px solid ' + buttonPrimaryDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonPrimaryLight,
          color: textLight,
          ':hover': {
            backgroundColor: buttonPrimaryDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        mediumPrimary: {
          width: '12.875rem',
          height: '2.5rem',
          ...titleTypography.title_xxs,
          textTransform: 'none',
          border: '1px solid ' + buttonPrimaryDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonPrimaryLight,
          color: textLight,
          ':hover': {
            backgroundColor: buttonPrimaryDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        bigPrimary: {
          width: '15.1875rem',
          height: '3rem',
          ...titleTypography.title_xs,
          textTransform: 'none',
          border: '1px solid ' + buttonPrimaryDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonPrimaryLight,
          color: textLight,
          ':hover': {
            backgroundColor: buttonPrimaryDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        smallSecondary: {
          width: '8.125rem',
          height: '1.875rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          border: '1px solid ' + buttonSecondaryDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonSecondaryLight,
          ':hover': {
            backgroundColor: buttonSecondaryDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        mediumSecondary: {
          width: '12.875rem',
          height: '2.5rem',
          ...titleTypography.title_xxs,
          textTransform: 'none',
          border: '1px solid ' + buttonSecondaryDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonSecondaryLight,
          ':hover': {
            backgroundColor: buttonSecondaryDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        bigSecondary: {
          width: '15.1875rem',
          height: '3rem',
          ...titleTypography.title_xs,
          textTransform: 'none',
          border: '1px solid ' + buttonSecondaryDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonSecondaryLight,
          ':hover': {
            backgroundColor: buttonSecondaryDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        smallRed: {
          width: '8.125rem',
          height: '1.875rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          color: textLight,
          border: '1px solid ' + buttonErrorDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonErrorLight,
          ':hover': {
            backgroundColor: buttonErrorDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        mediumRed: {
          width: '12.875rem',
          height: '2.5rem',
          ...titleTypography.title_xxs,
          textTransform: 'none',
          color: textLight,
          border: '1px solid ' + buttonErrorDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonErrorLight,
          ':hover': {
            backgroundColor: buttonErrorDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        bigRed: {
          width: '15.1875rem',
          height: '3rem',
          ...titleTypography.title_xs,
          textTransform: 'none',
          color: textLight,
          borderRadius: '0.25rem',
          border: '1px solid ' + buttonErrorDark,
          backgroundColor: buttonErrorLight,
          ':hover': {
            backgroundColor: buttonErrorDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        smallGreen: {
          width: '8.125rem',
          height: '1.875rem',
          ...bodyTypography.body_xxs_medium,
          textTransform: 'none',
          color: textLight,
          border: '1px solid ' + buttonSuccessDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonSuccessLight,
          ':hover': {
            backgroundColor: buttonSuccessDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        mediumGreen: {
          width: '12.875rem',
          height: '2.5rem',
          ...titleTypography.title_xxs,
          textTransform: 'none',
          color: textLight,
          border: '1px solid ' + buttonSuccessDark,
          borderRadius: '0.25rem',
          backgroundColor: buttonSuccessLight,
          ':hover': {
            backgroundColor: buttonSuccessDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
        bigGreen: {
          width: '15.1875rem',
          height: '3rem',
          ...titleTypography.title_xs,
          textTransform: 'none',
          color: textLight,
          borderRadius: '0.25rem',
          border: '1px solid ' + buttonSuccessDark,
          backgroundColor: buttonSuccessLight,
          ':hover': {
            backgroundColor: buttonSuccessDark,
          },
          ':disabled': {
            border: '1px solid ' + buttonGrayDark,
            backgroundColor: buttonGrayLight,
            color: textGrayDark,
          },
        },
      },
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'defaultCard' },
          style: {
            placeSelf: 'center',
            padding: '2rem',
            borderRadius: '1.25rem',
            border: '1px solid ' + neutralsN03,
          },
        },
      ],
    },
    MuiTypography: {
      defaultProps: {
        color: textDark,
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          '& .MuiToolbar-root': {
            paddingLeft: '0px !important',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root.Mui-focused': {
            color: textDark, // Color de la etiqueta cuando está enfocado
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'outlined',
        IconComponent: (props) => <KeyboardArrowDownIcon {...props} />,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <KeyboardArrowDownIcon />,
      },
      styleOverrides: {
        input: {
          padding: '1.5px 4px 7.5px 5px !important',
        },
      },
    },
    MUIDataTableHeadCell: {
      styleOverrides: {
        root: {
          color: textAccent + ' !important',
          fontWeight: 'bold !important',
        },
        toolButton: {
          color: textAccent + ' !important',
          fontWeight: 'bold !important',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.brandlessCheckbox': {
            color: brandlessIconBlack,
            padding: '0.25rem',
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          '&.brandlessRadio': {
            color: brandlessIconBlack,
            padding: '0.25rem',
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          '&.brandlessLink': {
            '&[disabled]': {
              color: brandlessTextDisabled,
              textDecoration: 'none',
              pointerEvents: 'none',
              ':hover': {
                color: brandlessTextDisabled,
              },
            },
            ...bodyTypography.caption_semibold,
            color: brandlessLinkBlue,
            textDecoration: 'underline',
            ':hover': {
              color: brandlessLinkBlue,
              '&.undelineNoe': {
                textDecoration: 'underline !important',
              },
            },
          },
          textDecoration: 'none',
          ':hover': {
            color: alertSuccess,
            textDecoration: 'underline',
            '&.undelineNoe': {
              textDecoration: 'none !important',
            },
            '& .MuiListItemIcon-root': {
              color: alertSuccess,
            },
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '0 !important',
        },
        content: {
          margin: '0 !important',
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: neutralsN03,
          borderLeftStyle: 'solid',
          borderLeftWidth: '2px',
          minHeight: '1.5rem',
          marginLeft: '0.85rem',
        },
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: {
          borderLeft: '2px solid ' + neutralsN03,
        },
      },
    },
    MUIDataTableToolbar: {
      styleOverrides: {
        actions: {
          display: 'flex',
          flexDirection: 'row',
          flex: 'initial',
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingRight: '1rem !important',
          },
        },
        toolbar: {
          '& .MuiInputBase-root': {
            boxShadow: 'none',
          },
        },
      },
    },
    /* descoment for standard variant 

     MuiInputAdornment: {
      styleOverrides: {
        root: {
            marginRight: '8px',
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
        borderRadius: '0.25rem',
       boxShadow: '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 1px 0px rgba(0, 0, 0, 0.20)',
       height: '3rem',
      paddingLeft:"0.8rem"
    },
 
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
        marginLeft: "0.8rem",
            marginTop: "0.4rem",
            },
 
      },
    }
    END standard variant */

    /* descoment for outlined variant END outlined variant */
    MuiInputLabel: {
      styleOverrides: {
        root: {
          marginTop: '-0.5rem',
        },
        shrink: {
          marginTop: '0rem !important',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '0.25rem',
          boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.2)',
          height: '40px',
          '& input:-webkit-autofill': {
            boxShadow: `0 0 0 100px transparent inset`,
            WebkitTextFillColor: 'inherit',
            transition: 'background-color 5000s ease-in-out 0s',
          },
        },
        input: {
          '&.FirstLetterUpper': {
            textTransform: 'capitalize',
          },
        },
        multiline: {
          height: 'auto',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: `rgba(0, 0, 0, 0.12) rgba(0, 0, 0, 0.12) ${primaryColor} rgba(0, 0, 0, 0.12) !important`,
            borderWidth: '1px 1px 1px 1px !important',
          },
          '&.Mui-focused:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: `rgba(0, 0, 0, 0.12) rgba(0, 0, 0, 0.12) ${primaryColor} rgba(0, 0, 0, 0.12) !important`,
            borderWidth: '1px 1px 1px 1px !important',
          },
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          borderRadius: '0.25rem',
          boxShadow: '2px 2px 5px rgba(0, 0, 0, 0.2)',
          height: '40px',
          cursor: 'pointer',
          '&.Mui-disabled': {
            cursor: 'default',
          },
        },
        input: {
          '&.FirstLetterUpper': {
            textTransform: 'capitalize',
          },
        },
        multiline: {
          height: 'auto',
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover:not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: `rgba(0, 0, 0, 0.12) rgba(0, 0, 0, 0.12) ${primaryColor} rgba(0, 0, 0, 0.12) !important`,
            borderWidth: '1px 1px 1px 1px !important',
          },
          '&.Mui-focused:not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline': {
            borderColor: `rgba(0, 0, 0, 0.12) rgba(0, 0, 0, 0.12) ${primaryColor} rgba(0, 0, 0, 0.12) !important`,
            borderWidth: '1px 1px 1px 1px !important',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          fontFamily: 'inherit',
        },
        html: {
          fontFamily:
            '"Urbanist", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
        },
        body: {
          fontFamily: 'inherit',
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': { width: '100%' },
        },
      },
    },
  },
})

export default theme
